import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, Tag, X } from "lucide-react";
import { toast } from "sonner";
import ImageUpload from "@/components/ImageUpload";

const emptyForm = { name: "", slug: "", description: "", logo_url: "", keywords: [], is_active: true };

export default function Brands() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [kwInput, setKwInput] = useState("");

  const fetchRows = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/brands?limit=100");
      setRows(data.items || []);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchRows(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setKwInput(""); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...emptyForm, ...r, keywords: Array.isArray(r.keywords) ? r.keywords : [] }); setKwInput(""); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      const { website: _drop, ...rest } = form; // never send legacy website field
      void _drop;
      const payload = { ...rest, keywords: form.keywords || [] };
      if (editing) await api.patch(`/brands/${editing.id}`, payload);
      else await api.post("/brands", payload);
      toast.success(editing ? "Brand updated" : "Brand created");
      setOpen(false); fetchRows();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };
  const del = async (r) => {
    if (!window.confirm(`Delete "${r.name}"?`)) return;
    try { await api.delete(`/brands/${r.id}`); toast.success("Deleted"); fetchRows(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const addKeyword = () => {
    const raw = kwInput.trim().toLowerCase();
    if (!raw) return;
    const tokens = raw.split(/[,\s]+/).filter((t) => t && !form.keywords.includes(t));
    if (tokens.length === 0) { setKwInput(""); return; }
    setForm((f) => ({ ...f, keywords: [...f.keywords, ...tokens] }));
    setKwInput("");
  };
  const dropKeyword = (kw) => setForm((f) => ({ ...f, keywords: f.keywords.filter((k) => k !== kw) }));

  const columns = [
    { key: "name", header: "Brand", render: (r) => (
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-secondary grid place-items-center overflow-hidden">
          {r.logo_url ? <img src={r.logo_url} alt="" className="w-full h-full object-cover" /> : <Tag className="w-4 h-4 text-muted-foreground" />}
        </div>
        <div className="min-w-0">
          <div className="font-medium truncate">{r.name}</div>
          <div className="text-xs text-muted-foreground truncate">{r.slug}</div>
        </div>
      </div>
    )},
    { key: "keywords", header: "Keywords", render: (r) => {
      const kws = Array.isArray(r.keywords) ? r.keywords : [];
      if (kws.length === 0) return <span className="text-muted-foreground text-xs">—</span>;
      return (
        <div className="flex flex-wrap gap-1 max-w-[420px]">
          {kws.slice(0, 6).map((k) => <Badge key={k} variant="secondary" className="text-[10px] font-mono">{k}</Badge>)}
          {kws.length > 6 && <span className="text-[11px] text-muted-foreground">+{kws.length - 6}</span>}
        </div>
      );
    }},
    { key: "is_active", header: "Status", className: "w-24",
      render: (r) => <Badge variant={r.is_active ? "default" : "secondary"} className={r.is_active ? "bg-accent hover:bg-accent" : ""}>{r.is_active ? "Active" : "Inactive"}</Badge> },
    { key: "actions", header: "", className: "w-24 text-right",
      render: (r) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); openEdit(r); }} data-testid={`edit-brand-${r.id}`}><Pencil className="w-4 h-4" /></Button>
          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); del(r); }} data-testid={`delete-brand-${r.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
        </div>
      )},
  ];

  return (
    <div className="p-8" data-testid="brands-page">
      <PageHeader title="Brands" subtitle="Manufacturers, private labels and search keywords"
        actions={<Button onClick={openCreate} className="btn-primary-glow" data-testid="add-brand-btn"><Plus className="w-4 h-4 mr-2" />New brand</Button>} />
      <DataTable columns={columns} rows={rows} loading={loading} onRowClick={openEdit} testId="brands-table" />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="brand-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit brand" : "New brand"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="brand-name" />
            </div>
            <ImageUpload
              value={form.logo_url || ""}
              onChange={(v) => setForm({ ...form, logo_url: v })}
              label="Brand logo"
              aspect="square"
              testId="brand-logo"
            />
            <div>
              <Label>Keywords / search tags</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={kwInput}
                  onChange={(e) => setKwInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addKeyword(); } }}
                  placeholder="Add: atta, oil, rice, sunflower…"
                  data-testid="brand-keyword-input"
                />
                <Button type="button" variant="outline" onClick={addKeyword} data-testid="brand-keyword-add">Add</Button>
              </div>
              {form.keywords.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2" data-testid="brand-keyword-list">
                  {form.keywords.map((kw) => (
                    <span key={kw} className="inline-flex items-center gap-1 rounded-full bg-secondary px-3 py-1 text-xs font-mono">
                      {kw}
                      <button onClick={() => dropKeyword(kw)} className="text-muted-foreground hover:text-destructive" data-testid={`brand-keyword-rm-${kw}`}>
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="text-[11px] text-muted-foreground mt-2">
                When a customer searches any of these words, this brand&apos;s products will appear. Also used inside the brand landing page.
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="brand-desc" />
            </div>
            <div className="flex items-center justify-between rounded-lg border px-3 py-2">
              <div>
                <div className="text-sm font-medium">Active</div>
                <div className="text-[11px] text-muted-foreground">Visible on storefront &amp; catalog</div>
              </div>
              <Switch checked={!!form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="brand-active" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || !form.name} className="btn-primary-glow" data-testid="save-brand-btn">
              {saving ? "Saving…" : editing ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
